import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { APPLICATION_STATUSES } from "@/components/ApplicationFields";
import { Package, Banknote, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const KIND = {
  material: { label: "Malzeme", icon: Package, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  investment: { label: "Yatırım", icon: Banknote, cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

const DeliveryRow = ({ r, onApplied }) => {
  const k = KIND[r.kind];
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      await api.put(`/${r.kind === "material" ? "material-usages" : "investments"}/${r.id}/applied`);
      toast.success(`"${r.name}" uygulandı olarak işaretlendi`);
      onApplied(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "İşaretlenemedi");
      setBusy(false);
    }
  };
  return (
    <div className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`overdue-delivery-${r.id}`}>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${k.cls}`}>
        <k.icon className="h-3 w-3" /> {k.label}
      </span>
      <div className="flex-1 min-w-[200px]">
        <Link to={`/koyler/${r.village_id}`} className="text-sm font-medium text-slate-900 hover:text-emerald-700 truncate block">
          {r.name}{r.quantity ? ` · ${r.quantity}` : ""}
        </Link>
        <div className="text-xs text-slate-500">{r.village_name}{r.location ? ` · ${r.location}` : ""}</div>
      </div>
      <div className="font-mono text-xs text-slate-600">Teslim: {formatDate(r.delivery_date)}</div>
      <div className="text-xs font-semibold text-rose-700 whitespace-nowrap" data-testid={`overdue-days-${r.id}`}>{r.days_late} gün gecikti</div>
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs border border-amber-200 bg-amber-50 text-amber-700">
        {APPLICATION_STATUSES[r.application_status] || "Durum belirtilmedi"}
      </span>
      <Button size="sm" onClick={apply} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 h-8"
              data-testid={`overdue-apply-${r.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Uygulandı
      </Button>
    </div>
  );
};

export const OverdueDeliveriesWidget = ({ title }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/stats/overdue-deliveries").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  return (
    <Card className="p-6 border border-slate-200 shadow-sm mt-6" data-testid="overdue-deliveries-card">
      <div className="mb-4">
        {title}
        <p className="text-xs text-slate-500 mt-1">Teslim tarihi geçtiği hâlde “Uygulandı” olarak işaretlenmemiş malzeme ve yatırımlar — en eski teslim tarihi en üstte</p>
      </div>
      {rows && rows.length > 0 && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium" data-testid="overdue-deliveries-alert">
          <AlertTriangle className="h-4 w-4" /> {rows.length} kayıt teslim tarihini geçti
        </div>
      )}
      {rows && rows.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="overdue-deliveries-empty">Teslim tarihi geçmiş, uygulanmamış kayıt yok.</div>
      )}
      {rows && rows.length > 0 && (
        <div className="divide-y divide-slate-100" data-testid="overdue-deliveries-list">
          {rows.map((r) => <DeliveryRow key={`${r.kind}-${r.id}`} r={r}
            onApplied={(x) => setRows((list) => list.filter((y) => !(y.kind === x.kind && y.id === x.id)))} />)}
        </div>
      )}
    </Card>
  );
};
